import React from "react";
import { useRouteError, isRouteErrorResponse, Link, useLocation } from "react-router-dom";
import { AlertCircle, Home, Search, RefreshCw, Bug } from "lucide-react";
import { Button } from "@/ui/components/primitives/button";
import { Card } from "@/ui/components/primitives/card";
import { Badge } from "@/ui/components/primitives/badge";

export function RouteErrorBoundary() {
  const error = useRouteError();
  const location = useLocation();

  // Enhanced error information
  const errorInfo = React.useMemo(() => {
    let errorMessage: string;
    let errorStatus: number | undefined;
    let errorType: 'network' | 'permission' | 'not-found' | 'server' | 'unknown';

    if (isRouteErrorResponse(error)) {
      errorStatus = error.status;
      errorMessage = error.statusText || error.data?.message || 'Unknown error';

      // Determine error type based on status
      if (error.status === 404) {
        errorType = 'not-found';
      } else if (error.status === 401 || error.status === 403) {
        errorType = 'permission';
      } else if (error.status >= 500) {
        errorType = 'server';
      } else if (error.status >= 400) {
        errorType = 'network';
      } else {
        errorType = 'unknown';
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
      errorType = 'unknown';
    } else if (typeof error === 'string') {
      errorMessage = error;
      errorType = 'unknown';
    } else {
      errorMessage = 'Unknown error occurred';
      errorType = 'unknown';
    }

    return { errorMessage, errorStatus, errorType };
  }, [error]);

  // Get user-friendly error information
  const getErrorDisplay = React.useCallback(() => {
    const { errorStatus, errorType, errorMessage } = errorInfo;

    switch (errorType) {
      case 'not-found':
        return {
          title: 'Page Not Found',
          description: 'The PayGate page you\'re looking for doesn\'t exist or has been moved.',
          icon: Search,
          badge: '🔍 Missing Page',
          badgeVariant: 'outline' as const
        };

      case 'permission':
        return {
          title: 'Access Denied',
          description: 'You don\'t have permission to access this PayGate feature. Please check your account permissions.',
          icon: AlertCircle,
          badge: '🔒 Access Denied',
          badgeVariant: 'destructive' as const
        };

      case 'server':
        return {
          title: 'Server Error',
          description: 'PayGate services are currently experiencing issues. Please try again in a few minutes.',
          icon: AlertCircle,
          badge: '🔧 Service Error',
          badgeVariant: 'destructive' as const
        };

      case 'network':
        return {
          title: 'Connection Error',
          description: 'Unable to connect to PayGate. Please check your internet connection.',
          icon: AlertCircle,
          badge: '🌐 Network Issue',
          badgeVariant: 'destructive' as const
        };

      default:
        return {
          title: 'Unexpected Error',
          description: 'An unexpected error occurred while loading this PayGate page.',
          icon: AlertCircle,
          badge: '⚠️ Application Error',
          badgeVariant: 'secondary' as const
        };
    }
  }, [errorInfo]);

  // Enhanced error logging
  React.useEffect(() => {
    const errorDetails = {
      error,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };


    // Send to error reporting service when available
    // @ts-ignore - error reporting service would be added here
    if (typeof window !== 'undefined' && window.errorReportingService) {
      // @ts-ignore
      window.errorReportingService.captureException(error, errorDetails);
    }
  }, [error, location, errorInfo]);

  const displayInfo = getErrorDisplay();
  const Icon = displayInfo.icon;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-lg p-6 text-center">
        {/* Error Icon and Status */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <Icon className="h-16 w-16 text-destructive" />
            {errorInfo.errorStatus && (
              <Badge className="absolute -top-2 -right-2 text-xs font-bold">
                {errorInfo.errorStatus}
              </Badge>
            )}
          </div>
        </div>

        {/* Error Title and Description */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-3">
            {displayInfo.title}
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            {displayInfo.description}
          </p>
        </div>

        {/* Error Type Badge */}
        <div className="mb-6">
          <Badge variant={displayInfo.badgeVariant} className="mb-4">
            {displayInfo.badge}
          </Badge>
        </div>

        {/* Requested Path Information */}
        <div className="mb-6 p-3 bg-muted rounded-lg border">
          <p className="text-xs text-muted-foreground">
            <strong>Requested:</strong> {location.pathname}
            {location.search && <span>{location.search}</span>}
          </p>
        </div>

        {/* Development Error Details */}
        {process.env.NODE_ENV === 'development' && (
          <details className="mb-6 text-left bg-muted p-4 rounded-lg border">
            <summary className="cursor-pointer text-sm font-medium text-foreground mb-3 flex items-center">
              <Bug className="h-4 w-4 mr-2" />
              Development Error Details
            </summary>
            <div className="space-y-2 text-xs">
              <div>
                <strong>Error Type:</strong> {errorInfo.errorType}
              </div>
              <div>
                <strong>Status:</strong> {errorInfo.errorStatus || 'N/A'}
              </div>
              <div>
                <strong>Message:</strong> {errorInfo.errorMessage}
              </div>
              <div>
                <strong>Path:</strong> {location.pathname}
              </div>
              {error instanceof Error && error.stack && (
                <div>
                  <strong>Stack Trace:</strong>
                  <pre className="mt-1 overflow-auto bg-background p-2 rounded border">
                    {error.stack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button asChild className="w-full" size="lg">
            <Link to="/dashboard">
              <Home className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>

          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="w-full"
            size="lg"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Page
          </Button>

          {/* Go Back Button */}
          {window.history.length > 1 && (
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="w-full"
              size="sm"
            >
              ← Go Back
            </Button>
          )}
        </div>

        {/* Support Information */}
        <div className="mt-6 pt-6 border-t">
          <p className="text-xs text-muted-foreground">
            If you believe this is an error, please{' '}
            <a
              href="mailto:support@paygate.sbs"
              className="text-primary hover:underline"
            >
              contact PayGate support
            </a>
            {' '}with details about what you were trying to access.
          </p>
        </div>
      </Card>
    </div>
  );
}